import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/api';
import { 
    FaCheckCircle, FaUpload, FaMapMarkerAlt, FaIdCard, 
    FaBriefcase, FaBuilding, FaUser, FaInfoCircle, FaArrowRight, FaArrowLeft
} from 'react-icons/fa';
import defaultAvatar from '../assets/images/farmerImage/default_image.png';
import { Map, MapMarker, MapControls } from '../components/ui/map';

const CompleteProfilePage = () => {
    const { user, refetchUser } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    
    // Dynamic Schema & Data
    const [schema, setSchema] = useState([]);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [formData, setFormData] = useState({}); // Stores all field values by name
    const [filePreviews, setFilePreviews] = useState({}); // Stores previews for images

    // Map Specific State (for Locked Location Step)
    const [mapCenter, setMapCenter] = useState([90.4125, 23.8103]);
    const [markerPos, setMarkerPos] = useState({ lng: 90.4125, lat: 23.8103 });
    const [locationText, setLocationText] = useState('');

    // Unified Categories Fetch (Moved to top level)
    const [allCategories, setAllCategories] = useState([]);
    useEffect(() => {
        apiClient.get('/categories').then(res => setAllCategories(res.data)).catch(console.error);
    }, []);

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                // 1. Fetch Schema
                const schemaRes = await apiClient.get(`/forms/schema?role=${user.role}`);
                let steps = schemaRes.data;
                console.log("Form Schema Loaded:", steps.length, "steps");
                
                // Ensure Location & Review steps
                const hasLocation = steps.find(s => s.is_locked);
                if (!hasLocation) {
                    steps.push({ id: 999, title: 'Location', description: 'Pin your address', is_locked: true, fields: [] });
                }
                steps.push({ id: 1000, title: 'Review', description: 'Final Check', is_review: true, fields: [] });
                setSchema(steps);

                // 2. Fetch Existing Profile Data
                try {
                    const profileRes = await apiClient.get(`/profiles/${user.userId}`);
                    const profile = profileRes.data;
                    console.log("Profile Data Loaded:", profile?.name);

                    if (profile) {
                        const initialData = {
                            name: profile.name || user.name || '',
                            date_of_birth: profile.date_of_birth ? new Date(profile.date_of_birth).toISOString().split('T')[0] : '',
                            gender: profile.gender || '',
                            nid_or_passport: profile.nid_or_passport || '',
                            id_document_url: profile.id_document_url || '',
                            picture: profile.picture || ''
                        };

                        if (profile.location) {
                            setLocationText(profile.location.granular_location || profile.location.address || '');
                            if (profile.location.lat && profile.location.lng) {
                                setMarkerPos({ lat: profile.location.lat, lng: profile.location.lng });
                                setMapCenter([profile.location.lng, profile.location.lat]);
                            }
                        }

                        if (user.role === 'worker' && profile.worker_details) {
                            Object.assign(initialData, profile.worker_details);
                        } else if (user.role === 'buyer' && profile.buyer_details) {
                            Object.assign(initialData, profile.buyer_details);
                        }

                        setFormData(initialData);
                        if (profile.picture) setFilePreviews(prev => ({ ...prev, picture: profile.picture }));
                    }
                } catch (profErr) {
                    console.error("Profile Fetch Error:", profErr);
                    // Fallback: Use data from Auth context if profile API fails
                    setFormData({ name: user.name || '' });
                }

            } catch (err) {
                console.error("Schema Load Error:", err);
                setError(`Failed to load profile configuration: ${err.message}`);
            } finally {
                setLoading(false);
            }
        };
        if (user?.userId) loadData();
    }, [user?.role, user?.userId]);

    // Field Change Handler
    const handleFieldChange = (name, value, type, config) => {
        if (type === 'file') {
            // Validation: Max Size
            const maxSizeMB = config?.maxSize || 5;
            if (value && value.size > maxSizeMB * 1024 * 1024) {
                alert(`File is too large. Max size is ${maxSizeMB}MB.`);
                return;
            }
            // Validation: Type
            const allowed = config?.allowedTypes;
            if (allowed && allowed.length > 0 && value) {
                const ext = value.name.split('.').pop().toLowerCase();
                if (!allowed.includes(ext)) {
                    alert(`Invalid file type. Allowed: ${allowed.join(', ')}`);
                    return;
                }
            }

            setFormData(prev => ({ ...prev, [name]: value }));
            if (value && value.type.startsWith('image/')) {
                setFilePreviews(prev => ({ ...prev, [name]: URL.createObjectURL(value) }));
            }
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    // Map Handlers
    const handleMarkerDrag = async (coords) => {
        setMarkerPos(coords);
        try {
            const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${coords.lat}&lon=${coords.lng}`);
            const data = await response.json();
            if (data && data.display_name) {
                setLocationText(data.display_name);
            }
        } catch (err) {
            console.error("Reverse geocoding failed", err);
        }
    };

    const handleGeolocate = (coords) => {
        setMarkerPos(coords);
        setMapCenter([coords.lng, coords.lat]);
        handleMarkerDrag(coords);
    };

    // Navigation
    const handleNext = () => {
        // Validation for current step
        const currentStep = schema[currentStepIndex];
        if (!currentStep.is_locked && !currentStep.is_review) {
            for (const field of currentStep.fields) {
                if (field.required && !formData[field.name]) {
                    alert(`Please fill required field: ${field.label}`);
                    return;
                }
            }
        }
        setCurrentStepIndex(prev => Math.min(prev + 1, schema.length - 1));
    };

    const handleBack = () => setCurrentStepIndex(prev => Math.max(prev - 1, 0));

    // Submission
    const handleSubmit = async () => {
        setSubmitting(true);
        setError('');

        try {
            // 1. Upload files first (if any)
            const processedData = { ...formData };
            const fileFields = Object.keys(formData).filter(key => formData[key] instanceof File);
            
            for (const key of fileFields) {
                const fileData = new FormData();
                fileData.append('profilePicture', formData[key]); // Endpoint expects 'profilePicture' key for all images currently
                const uploadRes = await apiClient.post('/upload/image', fileData, { headers: { 'Content-Type': 'multipart/form-data' } });
                processedData[key] = uploadRes.data.filePath;
            }

            // 2. Structure Payload
            // We need to map dynamic fields back to the specific DB columns expected by the backend
            // Or the backend needs to handle dynamic fields. 
            // CURRENTLY: Backend expects specific 'profile', 'worker', 'buyer' objects.
            // We'll map known keys and dump rest into a generic structure if backend supports it, 
            // OR we assume the Admin configured keys match DB columns (e.g. 'experience_year').
            
            const profileObj = {
                name: processedData.name || user.username,
                date_of_birth: processedData.date_of_birth,
                gender: processedData.gender,
                id_type: 'NID', // Default or from form
                id_number: processedData.nid_or_passport,
                picture: processedData.picture || processedData.id_document_url, // Fallback mapping
                id_document_url: processedData.id_document_url,
                location: {
                    address: locationText,
                    lat: markerPos.lat,
                    lng: markerPos.lng,
                    // Try to parse address parts if needed, or backend does it
                    granular_location: locationText
                }
            };

            // Remove mapped keys from generic worker/buyer object
            const usedKeys = ['name', 'date_of_birth', 'gender', 'nid_or_passport', 'picture', 'id_document_url'];
            const roleData = {};
            Object.keys(processedData).forEach(key => {
                if (!usedKeys.includes(key)) {
                    roleData[key] = processedData[key];
                }
            });

            const finalPayload = new FormData();
            finalPayload.append('profile', JSON.stringify(profileObj));
            
            if (user.role === 'worker') {
                finalPayload.append('worker', JSON.stringify(roleData));
            } else {
                finalPayload.append('buyer', JSON.stringify(roleData));
            }

            await apiClient.put('/profiles/complete', finalPayload, { headers: { 'Content-Type': 'multipart/form-data' } });
            await refetchUser();
            navigate(user.role === 'worker' ? '/worker-dashboard' : '/owner-dashboard');

        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || 'Failed to submit profile.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className="flex h-screen items-center justify-center"><div className="animate-spin h-10 w-10 border-4 border-emerald-500 rounded-full border-t-transparent"></div></div>;

    const currentStep = schema[currentStepIndex] || { title: '', fields: [] };

    // Helper: Dynamic Field Renderer
    const renderField = (field) => {
        const commonClasses = "w-full bg-slate-50 border-none rounded-2xl p-4 focus:ring-2 ring-emerald-500 transition-all font-medium text-slate-700 placeholder:text-slate-300";
        
        // Special Case: Hierarchical Skill Selector
        if (field.name === 'preferred_working_category') {
            return (
                <div className="space-y-6 border p-6 rounded-3xl bg-slate-50 custom-scrollbar shadow-inner max-h-96 overflow-y-auto">
                    {allCategories.filter(c => !c.parentId).map(industry => (
                        <div key={industry.id} className="space-y-3">
                            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                <span className="text-sm">{industry.Icon}</span> {industry.Name}
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {allCategories.filter(c => c.parentId === industry.id).map(service => (
                                    <label key={service.id} className={`flex items-center space-x-3 p-3 rounded-2xl cursor-pointer transition-all border ${formData[field.name]?.includes(service.Name) ? 'bg-emerald-600 border-emerald-500 text-white shadow-md' : 'bg-white border-slate-100 text-slate-600 hover:border-emerald-200'}`}>
                                        <input 
                                            type="checkbox" 
                                            className="hidden" 
                                            checked={formData[field.name]?.includes(service.Name)}
                                            onChange={(e) => {
                                                const current = formData[field.name] || [];
                                                const updated = e.target.checked 
                                                    ? [...current, service.Name] 
                                                    : current.filter(s => s !== service.Name);
                                                handleFieldChange(field.name, updated);
                                            }}
                                        />
                                        <span className="text-sm font-bold">{service.Name}</span>
                                        {formData[field.name]?.includes(service.Name) && <FaCheckCircle className="ml-auto" size={12}/>}
                                    </label>
                                ))}
                            </div>
                            <div className="h-px bg-slate-200/50 my-4 last:hidden"></div>
                        </div>
                    ))}
                </div>
            );
        }

        switch (field.type) {
            case 'text':
            case 'number':
            case 'date':
                return <input 
                    type={field.type} 
                    value={formData[field.name] || ''} 
                    onChange={e => handleFieldChange(field.name, e.target.value)} 
                    className={commonClasses} 
                    placeholder={field.label} 
                />;
            case 'textarea':
                return <textarea 
                    value={formData[field.name] || ''} 
                    onChange={e => handleFieldChange(field.name, e.target.value)} 
                    className={commonClasses} 
                    rows={3} 
                />;
            case 'select':
                return (
                    <select 
                        value={formData[field.name] || ''} 
                        onChange={e => handleFieldChange(field.name, e.target.value)} 
                        className={commonClasses}
                    >
                        <option value="">Select {field.label}</option>
                        {field.config?.options?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                );
            case 'file':
                return (
                    <div className="relative group border-2 border-dashed border-slate-200 rounded-2xl p-8 hover:border-emerald-500 hover:bg-emerald-50/30 transition-all text-center bg-white shadow-sm overflow-hidden cursor-pointer">
                        <input 
                            type="file" 
                            className="absolute inset-0 opacity-0 cursor-pointer z-10" 
                            onChange={e => handleFieldChange(field.name, e.target.files[0], 'file', field.config)} 
                        />
                        {filePreviews[field.name] ? (
                            <img src={filePreviews[field.name]} alt="Preview" className="w-20 h-20 object-cover rounded-xl mx-auto mb-2 shadow-md" />
                        ) : (
                            <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:bg-emerald-100 transition-all text-slate-400 group-hover:text-emerald-600">
                                <FaUpload size={24} />
                            </div>
                        )}
                        <p className="font-bold text-slate-700 text-sm">{formData[field.name] ? formData[field.name].name : `Upload ${field.label}`}</p>
                        <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-wide">
                            {field.config?.allowedTypes ? field.config.allowedTypes.join(', ') : 'Any'} • Max {field.config?.maxSize || 5}MB
                        </p>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col lg:flex-row font-sans">
            
            {/* Sidebar */}
            <div className="lg:w-80 bg-white border-r border-slate-200 p-8 flex flex-col justify-between shadow-xl z-20">
                <div className="space-y-8">
                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center text-white font-black text-xl shadow-lg">S</div>
                        <h1 className="text-xl font-black text-slate-800 tracking-tighter">Service<span className="text-emerald-600">24</span></h1>
                    </div>
                    
                    <div className="space-y-2">
                        {schema.map((s, idx) => (
                            <button 
                                key={idx}
                                onClick={() => {
                                    // Only allow jumping to visited steps or current
                                    if (idx <= currentStepIndex) setCurrentStepIndex(idx);
                                }}
                                className={`w-full flex items-center space-x-4 p-3 rounded-xl transition-all text-left ${
                                    idx === currentStepIndex 
                                    ? 'bg-emerald-50 text-emerald-700 shadow-sm ring-1 ring-emerald-100' 
                                    : idx < currentStepIndex 
                                        ? 'text-emerald-600 opacity-80' 
                                        : 'text-slate-400'
                                }`}
                            >
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs ${
                                    idx === currentStepIndex ? 'bg-emerald-600 text-white' : 'bg-slate-100'
                                }`}>
                                    {idx + 1}
                                </div>
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-wider">{s.title}</p>
                                </div>
                                {idx < currentStepIndex && <FaCheckCircle className="ml-auto text-emerald-500" />}
                            </button>
                        ))}
                    </div>
                </div>
                
                <div className="bg-blue-50 p-5 rounded-2xl border border-blue-100 flex gap-3">
                    <FaInfoCircle className="text-blue-500 mt-1 flex-shrink-0" />
                    <p className="text-xs text-blue-800 leading-relaxed font-medium">
                        Your profile helps us match you with the best opportunities. Please ensure all data is accurate.
                    </p>
                </div>
            </div>

            {/* Content */}
            <main className="flex-grow p-6 lg:p-12 xl:p-20 overflow-y-auto">
                <div className="max-w-3xl mx-auto">
                    
                    <div className="mb-10">
                        <h2 className="text-4xl font-black text-slate-800 tracking-tight mb-2">{currentStep.title}</h2>
                        <p className="text-slate-500 text-lg font-medium">{currentStep.description}</p>
                    </div>

                    <div className="bg-white rounded-[2.5rem] shadow-xl shadow-slate-200/50 border border-slate-100 p-8 lg:p-12 mb-10 min-h-[400px]">
                        
                        {/* Dynamic Fields Step */}
                        {!currentStep.is_locked && !currentStep.is_review && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                {currentStep.fields?.map(field => (
                                    <div key={field.id} className={field.type === 'file' || field.type === 'textarea' ? 'md:col-span-2' : ''}>
                                        <label className="text-xs font-black uppercase text-slate-400 tracking-widest ml-1 mb-2 block">
                                            {field.label} {field.required && <span className="text-red-500">*</span>}
                                        </label>
                                        {renderField(field)}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Location Step (Locked) */}
                        {currentStep.is_locked && (
                            <div className="space-y-6">
                                <div className="h-[400px] rounded-3xl overflow-hidden border-4 border-slate-100 shadow-lg relative">
                                    <Map 
                                        center={mapCenter} 
                                        zoom={13} 
                                        className="h-full w-full"
                                        onClick={handleMarkerDrag}
                                    >
                                        <MapControls showLocate onGeolocate={handleGeolocate} />
                                        <MapMarker 
                                            longitude={markerPos.lng} 
                                            latitude={markerPos.lat} 
                                            draggable 
                                            onDragEnd={handleMarkerDrag}
                                        />
                                    </Map>
                                    <div className="absolute top-4 left-4 right-14 bg-white/95 backdrop-blur p-4 rounded-xl shadow-lg border border-slate-200 z-[50]">
                                        <p className="text-[10px] font-black uppercase text-emerald-600 mb-1">Detected Address</p>
                                        <p className="text-sm font-bold text-slate-800 line-clamp-1">{locationText || "Drag the pin to set location..."}</p>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs font-black uppercase text-slate-400 tracking-widest ml-1 mb-2 block">Manual Address</label>
                                    <textarea 
                                        value={locationText} 
                                        onChange={e => setLocationText(e.target.value)} 
                                        className="w-full bg-slate-50 border-none rounded-2xl p-4 focus:ring-2 ring-emerald-500 transition-all font-medium text-slate-700" 
                                        rows="2" 
                                    />
                                </div>
                            </div>
                        )}

                        {/* Review Step */}
                        {currentStep.is_review && (
                            <div className="space-y-8 text-center py-10">
                                <div className="w-24 h-24 bg-emerald-600 rounded-[2rem] flex items-center justify-center mx-auto shadow-2xl shadow-emerald-200 rotate-12 mb-6">
                                    <FaCheckCircle className="text-white text-4xl" />
                                </div>
                                <div>
                                    <h3 className="text-3xl font-black text-slate-800">You're All Set!</h3>
                                    <p className="text-slate-500 mt-2 font-medium">Review your details before submitting.</p>
                                </div>
                                <div className="text-left bg-slate-50 p-6 rounded-3xl border border-slate-100 space-y-2">
                                    {Object.entries(formData).map(([key, val]) => {
                                        if (val === null || val === undefined || key === 'metadata' || key === 'picture' || key === 'id_document_url') return null;
                                        
                                        let displayVal = val;
                                        if (val instanceof File) displayVal = val.name;
                                        else if (Array.isArray(val)) displayVal = val.join(', ');
                                        else if (typeof val === 'object') return null; // Skip complex objects

                                        return (
                                            <div key={key} className="flex justify-between text-sm border-b border-slate-200 pb-2 last:border-0">
                                                <span className="font-bold text-slate-400 uppercase text-xs">{key.replace(/_/g, ' ')}</span>
                                                <span className="font-black text-slate-700 truncate max-w-[200px]">{displayVal}</span>
                                            </div>
                                        );
                                    })}
                                    <div className="flex justify-between text-sm pt-2">
                                        <span className="font-bold text-slate-400 uppercase text-xs">Location</span>
                                        <span className="font-black text-slate-700 truncate max-w-[200px]">{locationText}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>

                    {/* Footer Actions */}
                    <div className="flex justify-between items-center">
                        <button 
                            onClick={handleBack} 
                            disabled={currentStepIndex === 0 || submitting} 
                            className="px-8 py-4 rounded-2xl font-bold text-slate-400 hover:bg-white hover:shadow-sm transition-all disabled:opacity-0 flex items-center gap-2"
                        >
                            <FaArrowLeft /> Back
                        </button>

                        {currentStep.is_review ? (
                            <button 
                                onClick={handleSubmit} 
                                disabled={submitting} 
                                className="bg-emerald-600 text-white px-12 py-4 rounded-3xl font-black hover:bg-emerald-700 transition-all shadow-xl shadow-emerald-200 transform active:scale-95 disabled:opacity-70 flex items-center gap-3"
                            >
                                {submitting ? 'Processing...' : 'Submit Profile'} <FaCheckCircle />
                            </button>
                        ) : (
                            <button 
                                onClick={handleNext} 
                                className="bg-slate-900 text-white px-10 py-4 rounded-3xl font-black hover:bg-slate-800 transition-all shadow-xl hover:shadow-slate-300 transform active:scale-95 flex items-center gap-3"
                            >
                                Next Step <FaArrowRight />
                            </button>
                        )}
                    </div>

                    {error && <div className="mt-6 p-4 bg-red-50 text-red-600 text-center font-bold rounded-2xl text-sm">{error}</div>}
                </div>
            </main>
        </div>
    );
};

export default CompleteProfilePage;